import { FormFooter } from '@/components/basicComponents/formFooter';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { IOwner } from '@/components/dealCatalog-page/types';
import { UPDATE_INVESTMENT_MUTATION } from '@/components/dueDiligence-page/queries';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { ThreeColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FormFieldsWrapper } from '..';
import { OWNERS_QUERY } from '../../queries';
import Dialog, { DialogStatus } from '../modals/dialog';
import { getAllPossibleOwners, getOwnerEmailFromName, getOwnerValues } from '../utils';

interface IForm {
  investmentOwner: string;
  operationsOwner: string;
  complianceOwner: string;
}

const defaultFormValues: IForm = {
  investmentOwner: '',
  operationsOwner: '',
  complianceOwner: ''
};

const OWNER_TOOL_TIP = 'Each owner will be the default assignee for relevant tasks associated with this investment.';

interface OwnersModalPageProps {
  title: string;
  investmentId?: string;
  formData?: IForm | {};
  investmentRefetch: () => void;
  handleCloseEditModalPage: () => void;
}

export const OwnersModalPage = ({ title, investmentId, formData = {}, investmentRefetch, handleCloseEditModalPage }: OwnersModalPageProps) => {
  const { isMobile } = useResponsive();
  const {
    handleSubmit,
    getValues,
    control,
    reset,
    formState: { dirtyFields }
  } = useForm<IForm>({ defaultValues: defaultFormValues });

  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogStatus, setDialogStatus] = useState<DialogStatus>();
  const [allPossibleOwners, setAllPossibleOwners] = useState<IOwner[]>([]);
  const [investmentOwners, setInvestmentOwners] = useState<ISelectValue[]>([]);
  const [operationsOwners, setOperationsOwners] = useState<ISelectValue[]>([]);
  const [complianceOwners, setComplianceOwners] = useState<ISelectValue[]>([]);

  const [updateInvestment] = useMutation(UPDATE_INVESTMENT_MUTATION);

  const { loading: ownersLoading } = useQuery(OWNERS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    variables: {
      id: 'dafd1c4b-f85d-4e56-ad3c-1e60c0199349' // optional
    },
    onCompleted: (data) => {
      const { investmentOwners, operationsOwners, complianceOwners } = data.AllocationOwners;

      setInvestmentOwners(getOwnerValues(investmentOwners ?? [])?.map((owner) => createSimpleSelectOption(owner)) || []);
      setOperationsOwners(getOwnerValues(operationsOwners ?? [])?.map((owner) => createSimpleSelectOption(owner)) || []);
      setComplianceOwners(getOwnerValues(complianceOwners ?? [])?.map((owner) => createSimpleSelectOption(owner)) || []);

      setAllPossibleOwners(
        getAllPossibleOwners({
          investmentOwners,
          operationsOwners,
          complianceOwners
        })
      );
    }
  });

  useEffect(() => {
    reset((defaultFormData) => ({ ...defaultFormData, ...formData }));
  }, [formData]);

  const onCloseDialog = () => setDialogOpen(false);
  const onDialogSuccess = () => {
    reset();
    onCloseDialog();
    handleCloseEditModalPage();
  };

  const onUpdateInvestment = async (data: IForm) => {
    if (!investmentId) return;
    try {
      const { investmentOwner, operationsOwner, complianceOwner } = data;
      setDialogStatus(DialogStatus.LOADING);
      setDialogOpen(true);

      await updateInvestment({
        variables: {
          data: {
            id: investmentId,
            ...(dirtyFields?.investmentOwner && { investmentOwnerEmail: getOwnerEmailFromName(investmentOwner, allPossibleOwners) }),
            ...(dirtyFields?.operationsOwner && { operationsOwnerEmail: getOwnerEmailFromName(operationsOwner, allPossibleOwners) }),
            ...(dirtyFields?.complianceOwner && { complianceOwnerEmail: getOwnerEmailFromName(complianceOwner, allPossibleOwners) })
          }
        }
      });

      investmentRefetch();
      setDialogStatus(DialogStatus.SUCCESS);
      setDialogOpen(true);
    } catch (error) {
      setDialogStatus(DialogStatus.FAILED);
      setDialogOpen(true);
    }
  };

  if (ownersLoading) return <LoaderOnWholeScreen />;

  return (
    <MainWrap>
      <Dialog
        isOpen={isDialogOpen}
        onClose={onCloseDialog}
        onSuccess={onDialogSuccess}
        reFetch={() => onUpdateInvestment(getValues())}
        status={dialogStatus}
      />
      <Header modalControl={<GoBackButton handleClose={handleCloseEditModalPage} backToTitle={''} />} />
      <PageTitle title={title ?? ''} />
      <PaddingWrap>
        <Form id="ownersForm" onSubmit={handleSubmit(onUpdateInvestment, () => window.scrollTo(0, 0))}>
          <FormFieldsWrapper>
            <ThreeColumnWrapper isMobile={isMobile}>
              <Controller
                name="investmentOwner"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Investment Owner is a required field'
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    value={value}
                    setValue={onChange}
                    options={[{ value: '', label: 'Select' }, ...investmentOwners]}
                    errorText={error?.message}
                    size="md"
                    label="Investment Owner"
                    tooltip={OWNER_TOOL_TIP}
                    tooltipWidth={300}
                    isRequiredField
                  />
                )}
              />
              <Controller
                name="operationsOwner"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Operations Owner is a required field'
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    value={value}
                    setValue={onChange}
                    options={[{ value: '', label: 'Select' }, ...operationsOwners]}
                    errorText={error?.message}
                    size="md"
                    label="Operations Owner"
                    tooltip={OWNER_TOOL_TIP}
                    tooltipWidth={300}
                    isRequiredField
                  />
                )}
              />

              <Controller
                name="complianceOwner"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Compliance Owner is a required field'
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    value={value}
                    setValue={onChange}
                    options={[{ value: '', label: 'Select' }, ...complianceOwners]}
                    errorText={error?.message}
                    size="md"
                    label="Compliance Owner"
                    tooltip={OWNER_TOOL_TIP}
                    tooltipWidth={300}
                    isRequiredField
                  />
                )}
              />
            </ThreeColumnWrapper>
          </FormFieldsWrapper>
          <FormFooter onCancel={handleCloseEditModalPage} disableSaveButton={false} />
        </Form>
      </PaddingWrap>
    </MainWrap>
  );
};

const Form = styled.form`
  padding: 50px 0;
  width: 100%;
  background: ${({ theme }) => theme.layer.base};
`;
